import { Box, type Palette, Popover, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { ReactComponent as MarginalIcon } from 'assets/marginal.svg'
import { ReactComponent as SatisfyIcon } from 'assets/statusSatisfy.svg'
import { ReactComponent as StatusUnsatisfy } from 'assets/statusUnsatisfy.svg'
import React, { type ReactElement, useRef, useState } from 'react'
import { pluralize } from 'utils/functions'

import { DecisionStatus, QuestionType } from '../../../vendor.types'

import styles from './DecisionMechanism.module.scss'

interface ClickedItem {
  status: DecisionStatus
  boundaries?: {
    marginalBoundary: any
    satisfactoryBoundary: any
  }
  index: number
  questionType: QuestionType
  value: string
}

interface QuestionRowProps {
  index: number
  questionName: string
  answer: string
  status: DecisionStatus
  boundaries?: {
    marginalBoundary: any
    satisfactoryBoundary: any
  }
  questionType: QuestionType
}

interface PopoverText {
  marginal?: string
  satisfactory?: string
  unsatisfactory?: string
}

const getIcon = (status: string, palette: Palette): JSX.Element => {
  switch (status) {
    case DecisionStatus.satisfactory:
      return (
        <div className={styles.satisfactoryIcon}>
          <SatisfyIcon height="23px" width="23px" fill={palette.charts.green200.main} />
        </div>
      )
    case DecisionStatus.unsatisfactory:
      return (
        <div className={styles.unsatisfactoryIcon}>
          <StatusUnsatisfy height="20px" width="20px" />
        </div>
      )
    case DecisionStatus.marginal:
      return (
        <div className={styles.marginalIcon}>
          <MarginalIcon height="18px" width="18px" fill={palette.white.main} />
        </div>
      )
    case DecisionStatus.unknown:
      return (
        <div className={styles.marginalIcon}>
          <MarginalIcon height="18px" width="18px" fill={palette.gray.main} />
        </div>
      )
    default:
      return <></>
  }
}

const getColorByStatus = (status: string, palette: Palette): string => {
  switch (status) {
    case DecisionStatus.satisfactory:
      return palette.charts.green200.main
    case DecisionStatus.unsatisfactory:
      return palette.charts.red.main
    case DecisionStatus.marginal:
      return palette.charts.orange400.main
    case DecisionStatus.unknown:
      return palette.gray.main
    default:
      return palette.white.main
  }
}

const getPopoverText = (clickedItem: ClickedItem | null, questionType?: QuestionType): PopoverText | null => {
  switch (questionType) {
    case QuestionType.EMPLOYEE_COUNT:
      return {
        marginal: `zaposlen${pluralize(clickedItem?.boundaries?.satisfactoryBoundary, '', 'ih')}`,
        satisfactory: `zaposlen${pluralize(clickedItem?.boundaries?.satisfactoryBoundary, '', 'ih')}`,
        unsatisfactory: `zaposlen${pluralize(clickedItem?.boundaries?.marginalBoundary, '', 'ih')}`,
      }
    case QuestionType.ACCOUNT_BLOCKED:
    case QuestionType.ACCOUNT_BLOCKED_RECENTLY:
      return {
        satisfactory: clickedItem?.boundaries?.satisfactoryBoundary ? 'Da' : 'Ne',
        unsatisfactory: !clickedItem?.boundaries?.satisfactoryBoundary ? 'Da' : 'Ne',
      }
    case QuestionType.AVERAGE_MARGIN:
    case QuestionType.INCOME_TREND:
      return {
        marginal: '%',
        satisfactory: '%',
        unsatisfactory: '%',
      }
    case QuestionType.INDUSTRY:
      return {
        unsatisfactory: 'Ostale',
        satisfactory: 'Poželjne šifre delatnosti',
      }
    default:
      return null
  }
}

const QuestionRow = ({
  index,
  questionName,
  answer,
  status,
  boundaries,
  questionType,
}: QuestionRowProps): ReactElement => {
  const { palette } = useTheme()
  const [anchorElContextMenu, setAnchorElContextMenu] = useState<HTMLElement | null>(null)
  const [clickedItem, setClickedItem] = useState<ClickedItem | null>(null)
  const itemStatusRef = useRef(null)

  let closeTimer: NodeJS.Timeout

  const handleClose = (): void => {
    closeTimer = setTimeout(() => {
      setAnchorElContextMenu(null)
    }, 100)
  }

  const handleContextMenuClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, item: ClickedItem): void => {
    clearTimeout(closeTimer)
    setClickedItem(item)
    setAnchorElContextMenu(e.currentTarget as HTMLElement)
  }

  const getAnswer = (value: string, questionType: QuestionType, status: DecisionStatus): string => {
    if (questionType === QuestionType.INCOME_TREND) {
      switch (status) {
        case DecisionStatus.unsatisfactory:
          return 'Negativan'
        case DecisionStatus.marginal:
          return 'Stagnacija'
        case DecisionStatus.satisfactory:
          return 'Rast'
        case DecisionStatus.unknown:
          return 'Nema podataka'
        default:
          return ''
      }
    }
    return value
  }

  return (
    <div className={styles.rowItem}>
      <div className={styles.rowNumber}>
        <Typography variant="subtitle1">{index}</Typography>
      </div>
      <div className={styles.itemText}>
        <Typography variant="subtitle1">{questionName}</Typography>
        <Typography variant="subtitle1" sx={{ color: getColorByStatus(status, palette) }}>
          {getAnswer(answer, questionType, status)}
        </Typography>
      </div>
      <div
        ref={itemStatusRef}
        className={styles.itemStatus}
        onMouseEnter={(e) => {
          handleContextMenuClick(e, {
            status,
            boundaries,
            index,
            value: answer,
            questionType,
          })
        }}
        onMouseLeave={handleClose}
      >
        {getIcon(status, palette)}
      </div>
      <Popover
        className={styles.popper}
        open={!!anchorElContextMenu}
        onClose={handleClose}
        anchorEl={anchorElContextMenu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box className={styles.popperMenu} sx={{ backgroundColor: palette.blue700.main }}>
          <div className={styles.listItem}>
            <div className={styles.row}>
              <div
                className={`${styles.satisfy} ${styles.smallSquare} ${
                  clickedItem?.status !== DecisionStatus.satisfactory ? styles.opacity : ''
                }`}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: palette.white.main,
                  opacity: clickedItem?.status === DecisionStatus.satisfactory ? 1 : 0.5,
                }}
              >
                {clickedItem?.boundaries?.marginalBoundary !== null &&
                clickedItem?.boundaries?.marginalBoundary !== undefined
                  ? '≥ '
                  : ''}
                {questionType !== QuestionType.INDUSTRY && clickedItem?.boundaries?.satisfactoryBoundary}{' '}
                {getPopoverText(clickedItem, clickedItem?.questionType)?.satisfactory}
              </Typography>
            </div>
            {clickedItem?.boundaries?.marginalBoundary !== null &&
              clickedItem?.boundaries?.marginalBoundary !== undefined && (
                <div className={styles.row}>
                  <div
                    className={`${styles.partiallySatisfy} ${styles.smallSquare} ${
                      clickedItem?.status !== DecisionStatus.marginal ? styles.opacity : ''
                    }`}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: palette.white.main,
                      opacity: clickedItem?.status === DecisionStatus.marginal ? 1 : 0.5,
                    }}
                  >
                    {clickedItem?.boundaries?.marginalBoundary}
                    {' - '}
                    {clickedItem?.boundaries?.satisfactoryBoundary}{' '}
                    {getPopoverText(clickedItem, clickedItem?.questionType)?.marginal}
                  </Typography>
                </div>
              )}
            <div className={styles.row}>
              <div
                className={`${styles.unsatisfy} ${styles.smallSquare} ${
                  clickedItem?.status !== DecisionStatus.unsatisfactory ? styles.opacity : ''
                }`}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: palette.white.main,
                  opacity: clickedItem?.status === DecisionStatus.unsatisfactory ? 1 : 0.5,
                }}
              >
                {clickedItem?.boundaries?.marginalBoundary !== null &&
                  clickedItem?.boundaries?.marginalBoundary !== undefined &&
                  '< '}
                {clickedItem?.boundaries?.marginalBoundary}{' '}
                {getPopoverText(clickedItem, clickedItem?.questionType)?.unsatisfactory}
              </Typography>
            </div>
          </div>
        </Box>
      </Popover>
    </div>
  )
}

export default QuestionRow
